import type { Permission } from '../../../../api/types/permissions';
import type { TenantRoleListItemLocal } from '../../../../api/types/TenantLocal';

/** Espejo de `INTEGRATION_ADMIN_PERMISSIONS` del backend: los permisos que una integración no puede portar. */
export const INTEGRATION_ADMIN_PERMISSIONS: readonly Permission[] = [
    'platform.integration.view',
    'platform.integration.manage',
    'platform.integration.key.issue',
    'platform.integration.key.revoke',
];

/**
 * Candado de exhaustividad: si el backend agrega un `platform.integration.*` nuevo, `sync-api-types`
 * lo trae al enum y este `Record` deja de compilar hasta que alguien lo clasifique a mano.
 */
type PlatformIntegrationPermission = Extract<Permission, `platform.integration.${string}`>;
const CLASSIFIED: Record<PlatformIntegrationPermission, true> = {
    'platform.integration.view': true,
    'platform.integration.manage': true,
    'platform.integration.key.issue': true,
    'platform.integration.key.revoke': true,
};
void CLASSIFIED;

/** Motivo por el que un rol no se le puede dar a una integración. `null` ⇒ sí se puede. */
export type NotAssignableReason = 'ADMIN' | 'PLATFORM' | null;

/**
 * Reproduce client-side las dos guardas del backend para no ofrecer un rol que va a dar 422.
 * El backend las re-enforza; esto es UX, no seguridad.
 */
export function notAssignableReason(role: TenantRoleListItemLocal): NotAssignableReason {
    const permissions = role.permissions ?? [];
    if (permissions.some((p) => INTEGRATION_ADMIN_PERMISSIONS.includes(p))) return 'ADMIN';
    if (role.scope === 'PLATFORM') return 'PLATFORM';
    return null;
}

export const NOT_ASSIGNABLE_EXPLANATION: Record<Exclude<NotAssignableReason, null>, string> = {
    ADMIN: 'Otorga permisos para administrar integraciones — una integración con ese rol podría emitirse llaves a sí misma.',
    PLATFORM: 'Es un rol de plataforma. Una integración nunca puede tener alcance de plataforma.',
};
